import { useContext } from 'react'
import { Button, IconButton, Typography, useTheme } from '@material-ui/core'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import ArrowBackIcon from '@material-ui/icons/ArrowBackIosNew'
import StarsIcon from '@material-ui/icons/Stars'
import useStyles from './LegacyStyles'
import useMuseStyles from '@/themes/useMuseStyles'
import ModalCompatTooltip from '@/UIComponents/ModalCompatTooltip'
import * as Constants from '@/services/Constants'
import { AppContext, AppContextType } from '@/AppContext'
import useMediaSourceActions from '@/functionalities/MediaSources/useMediaSourceActions'
import { useTranslation } from 'react-i18next'
import useAnalytics from '@/services/useAnalytics'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import ElectronIPC from '@/services/ElectronIPC'
import useUserContextRef from '@/services/useUserContextRef'
import MenuIcon from '@/CustomIcons/MenuIcon'

function Header({
    isIndependentModal,
    currMenuSelection,
    title,
    onGoBack,
    onMenuClick,
}: {
    isIndependentModal: boolean
    currMenuSelection: any
    title: string
    onGoBack: () => void
    onMenuClick?: () => void
}) {
    const gclasses = useMuseStyles(useTheme())
    const classes = useStyles(useTheme())

    const { t } = useTranslation()

    const { logUserActivityWithContext } = useAnalytics()

    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { userStateRef } = useUserContextRef()

    const { refreshMediaSourcesWithDebounce } = useMediaSourceActions()

    // Do not show the refresh button on these menus:
    const noRefreshButtonMenus: { [key: string]: boolean } = {
        recording: true,
        'session-recording': true,
        session: true,
        'breakout-sessions': true,
        account: true,
        community: true,
        tutorial: true,
    }

    if (currMenuSelection === 'about') {
        return null
    }

    const isPro = Constants.proPolicies.includes(userStateRef.current.profile?.policy?._id || '')

    return (
        <div className="flex items-center justify-between relative p-0 min-h-[40px] max-h-[40px] border-b border-[#3F3F3F]">
            <Button className={classes.backArrow} style={{}} onClick={onGoBack}>
                <ArrowBackIcon style={{ width: '18px', color: '#f9cc04' }} />
            </Button>
            <div className="relative">
                <Typography variant="h1" className="m-0! text-[18px]! capitalize">
                    {title}
                </Typography>
                {!noRefreshButtonMenus[currMenuSelection] && (
                    <div className="absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2">
                        <ModalCompatTooltip content={t('Update list of devices')}>
                            <IconButton
                                className={gclasses.buttonNoLoadFlash}
                                style={{ padding: 0 }}
                                onClick={() => {
                                    refreshMediaSourcesWithDebounce()
                                    setTimeout(() => {
                                        appDispatch({
                                            type: 'root_snackbar_history_append',
                                            value: {
                                                severity: 'success',
                                                message: t('Updated device list!'),
                                            },
                                        })
                                    }, 100)
                                    logUserActivityWithContext(AnalyticsEvents.REFRESHED_DEVICE_LIST)
                                }}
                            >
                                <AutorenewIcon color="primary" style={{ fill: 'rgb(249, 204, 4)', width: '18px' }} />
                            </IconButton>
                        </ModalCompatTooltip>
                    </div>
                )}
            </div>

            <div className="min-w-[64px] flex items-center justify-center">
                <button onClick={onMenuClick} className="flex md:hidden items-center justify-center">
                    <MenuIcon stroke="#F9CC04" />
                </button>
                {!isPro && (
                    <div className="hidden md:flex w-full">
                        <ModalCompatTooltip
                            content={userStateRef.current?.isGuest ? t('Create Account') : t('Upgrade to Muse Pro')}
                        >
                            <Button
                                type="submit"
                                variant="outlined"
                                color="primary"
                                onClick={() => {
                                    if (userStateRef.current?.isGuest) {
                                        logUserActivityWithContext(AnalyticsEvents.CLICKED_CREATE_ACCOUNT_BUTTON)
                                        ElectronIPC.openLinkInBrowser(Constants.links.signup_link)
                                    } else {
                                        logUserActivityWithContext(AnalyticsEvents.CLICKED_UPGRADE_BUTTON)
                                        ElectronIPC.openLinkInBrowser(Constants.links.muse_pro_plans_link)
                                    }
                                }}
                                className={classes.upgradeButton}
                                size="medium"
                            >
                                {userStateRef.current?.isGuest ? (
                                    t('Create Account')
                                ) : (
                                    <>
                                        <StarsIcon style={{ margin: '0 3px', fontSize: '1rem' }} /> {t('Upgrade')}
                                    </>
                                )}
                            </Button>
                        </ModalCompatTooltip>
                    </div>
                )}
            </div>
        </div>
    )
}

export default Header
